/**
 * ContactPicker — searchable single-contact selector backed by server-side search.
 *
 * The dropdown is portaled to document.body with fixed positioning (mirrors Select.jsx) — it
 * must NOT render as a normal absolutely-positioned child, since callers like ContactDetailModal
 * put this inside a scrolling (overflow: auto) container that would otherwise clip/cut it off.
 *
 * Props:
 *   value      — selected contact id ('' = none)
 *   name       — display name of the selected contact
 *   seed       — optional pre-loaded contact records to show before the user types
 *   inputStyle — style object for the text input (defaults to a neutral DM Sans input)
 *   menuZIndex — the dropdown's layer (default 9000); raise it inside a dialog stacked higher
 *   onChange(id, name) — called when selection changes; id='' to clear
 *                        (third arg: the picked contact record, for callers that want its fields)
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { searchContacts } from '../../services/contacts.js'

function label(c) {
  const f = (c && c.fields) || {}
  return f['Name'] || [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || (c && c.id) || ''
}

const BASE_INPUT = {
  width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)',
  padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
  background: 'var(--aj-bg-subtle)', boxSizing: 'border-box',
}

const ST = {
  chip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  link: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#2563eb', letterSpacing: '0.03em' },
  row:  { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', padding: '7px 10px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' },
  msg:  { padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' },
}

export default function ContactPicker({ value, name, seed, inputStyle, onChange, menuZIndex = 9000 }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)   // null = browse (show seed), array = search results
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, above: false })
  const debRef = useRef(null)
  const inputRef = useRef(null)
  const menuRef = useRef(null)
  const iStyle = inputStyle || BASE_INPUT

  function runSearch(q) {
    clearTimeout(debRef.current)
    if (!q.trim()) { setResults(null); setSearching(false); return }
    setSearching(true)
    debRef.current = setTimeout(async () => {
      try { const { contacts: recs } = await searchContacts(q.trim()); setResults(recs || []) }
      catch { setResults([]) }
      finally { setSearching(false) }
    }, 300)
  }

  /* ── Position the portaled dropdown against the input, flipping above if there's no room below ── */
  useEffect(() => {
    if (!open) return
    const rect = inputRef.current?.getBoundingClientRect()
    if (!rect) return
    const EST_H = 240
    const above = window.innerHeight - rect.bottom < EST_H + 6 && rect.top > EST_H + 6
    setPos({ top: above ? rect.top : rect.bottom, left: rect.left, width: rect.width, above })
  }, [open])

  /* ── Close on outside click ───────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return
    function handler(e) {
      if (!inputRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  /* ── Close on scroll originating outside the menu (e.g. the modal itself scrolling) ─────────── */
  useEffect(() => {
    if (!open) return
    function handler(e) {
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    window.addEventListener('scroll', handler, true)
    return () => window.removeEventListener('scroll', handler, true)
  }, [open])

  function pick(c) {
    onChange(c.id, label(c), c)
    setOpen(false)
    setQuery('')
    setResults(null)
  }

  // Selected, not actively changing → compact chip with change / clear.
  if (value && !open) {
    return (
      <div style={{ ...iStyle, ...ST.chip }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || '(selected contact)'}</span>
        <span style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <button type="button" style={ST.link} onClick={() => { setOpen(true); setQuery(''); setResults(null) }}>change</button>
          <button type="button" style={{ ...ST.link, color: '#c0392b' }} onClick={() => onChange('', '')}>clear</button>
        </span>
      </div>
    )
  }

  const hasSeed = Array.isArray(seed) && seed.length > 0
  const list = results != null ? results : (seed || [])
  const emptyMsg = results != null ? 'No matches.' : hasSeed ? 'No contacts.' : 'Type to search contacts…'

  const menuStyle = {
    position: 'fixed',
    ...(pos.above ? { bottom: window.innerHeight - pos.top + 4 } : { top: pos.top + 4 }),
    left: pos.left, width: pos.width,
    background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 240, overflowY: 'auto', zIndex: menuZIndex,
  }

  return (
    <>
      <input
        ref={inputRef}
        style={iStyle} autoFocus placeholder="Search contacts by name…" value={query}
        onChange={e => { setQuery(e.target.value); runSearch(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
      />
      {open && createPortal(
        <div ref={menuRef} style={menuStyle}>
          <button type="button" style={{ ...ST.row, color: 'var(--aj-text-muted)' }} onMouseDown={e => e.preventDefault()}
            onClick={() => { onChange('', ''); setOpen(false) }}>— none —</button>
          {searching && <div style={ST.msg}>Searching…</div>}
          {!searching && list.length === 0 && <div style={ST.msg}>{emptyMsg}</div>}
          {!searching && list.map(c => (
            <button key={c.id} type="button" style={ST.row} onMouseDown={e => e.preventDefault()}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
              onClick={() => pick(c)}>
              {label(c)}
              {c.fields?.['Email'] && <span style={{ color: 'var(--aj-text-muted)', marginLeft: 8, fontSize: 11 }}>{c.fields['Email']}</span>}
            </button>
          ))}
          {results == null && hasSeed && seed.length >= 100 && (
            <div style={{ ...ST.msg, fontStyle: 'italic' }}>Showing first {seed.length}. Type to search all contacts.</div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
